import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, queryAll, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import typia from 'typia'
import { logValidationFailure } from 'components/shared/parsing/parsing.js'
import { style } from './store-stock-list.style.js'
import { STORE_LOCATOR_STORE, StoreLocatorStore, StoresList } from 'components/discovery/store-locator/store-locator-store.js'
import { InternalStore } from 'components/discovery/store-locator/typing/store-locator.js'
import { GetOutletStockRequest, OUTLET_STOCK_API, type OutletStockApi, type OutletStockResponse } from 'components/discovery/pdp/store-stock/api/outlet-stock-api.js'
import { InternalStoreStockLocation } from 'components/discovery/pdp/store-stock/typing/store-stock-types.js'
import 'components/discovery/store-locator/components/store-location/store-location.js'
import type { CSCStoreLocation, StoreLocationSelectedEvent } from 'components/discovery/store-locator/components/store-location/store-location.js'
import 'components/discovery/product/stock-indication/stock-indication.js'

const toStockLocation = (store: InternalStore, previousStock: number | null = null): InternalStoreStockLocation => ({
  ...store,
  stock: previousStock,
})

const showsDistanceFor = (mapCoordinateType: string | undefined): boolean =>
  ['current_location', 'autocomplete'].includes(mapCoordinateType ?? '')

@customElement('csc-store-stock-list')
export class CSCStoreStockList extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(STORE_LOCATOR_STORE)
  private storeLocatorStore!: StoreLocatorStore

  @inject(OUTLET_STOCK_API)
  private outletStockApi!: OutletStockApi

  @property({ type: Number, attribute: 'product-id' })
  productId!: number

  @property({ type: Boolean, attribute: 'only-in-stock' })
  onlyInStock = false

  @state()
  private locations: InternalStoreStockLocation[] | null = null

  @state()
  private showDistance = false

  @state()
  private selectedStoreId: number | null = null

  @queryAll('csc-store-location')
  private cards!: CSCStoreLocation[]

  constructor() {
    super()

    createEffect(() => {
      this.showDistance = showsDistanceFor(this.storeLocatorStore.mapCoordinates()?.type)
      this.selectedStoreId = this.storeLocatorStore.selectedStore()?.id ?? null
      this.syncLocations(this.storeLocatorStore.stores())

      const selectedCard = Array.from(this.cards).find((card) => card.store.id === this.selectedStoreId)
      selectedCard?.scrollIntoView(false)
    })
  }

  private lastProcessedStoresList: StoresList | null = null

  private syncLocations = (storesList: StoresList): void => {
    if (storesList.origin === 'preload') return
    if (storesList === this.lastProcessedStoresList) return
    this.lastProcessedStoresList = storesList

    const previousStockById = new Map((this.locations ?? []).map((location) => [location.id, location.stock]))

    const locations = storesList.stores.map((store) => toStockLocation(store, previousStockById.get(store.id) ?? null))
    this.locations = locations

    locations
      .filter((location) => !previousStockById.has(location.id))
      .forEach(this.loadStockFor)

    const [firstVisibleLocation] = this.visibleLocations
    if (this.showDistance && this.selectedStoreId === null && firstVisibleLocation) {
      this.storeLocatorStore.selectStore(firstVisibleLocation)
    }
  }

  private loadStockFor = async (location: InternalStoreStockLocation) => {
    let stock = 0

    try {
      const response = await this.outletStockApi.getStock(
        new GetOutletStockRequest(this.productId, location.id)
      )
      const validated = typia.validate<OutletStockResponse>(response)

      if (!validated.success) {
        logValidationFailure(`<csc-store-stock-list> stock for outlet ${location.id}`, validated.errors)
      } else {
        stock = validated.data.stock
      }
    } catch (error) {
      console.error(`<csc-store-stock-list> failed to fetch stock for outlet ${location.id}`, error)
    }

    this.locations = (this.locations ?? []).map((existing) =>
      existing.id === location.id ? { ...existing, stock } : existing
    )
  }

  private hasStock = (location: InternalStoreStockLocation): boolean => (location.stock ?? 0) > 0

  private sortByName = (locations: InternalStoreStockLocation[]): InternalStoreStockLocation[] =>
    [...locations].sort((a, b) => a.name.localeCompare(b.name))

  private get visibleLocations(): InternalStoreStockLocation[] {
    const locations = this.locations ?? []
    const filtered = this.onlyInStock ? locations.filter(this.hasStock) : locations

    return this.showDistance ? filtered : this.sortByName(filtered)
  }

  private handleLocationSelected = (event: StoreLocationSelectedEvent): void => {
    this.storeLocatorStore.selectStore(event.store)
  }

  private renderLoading = () => html`
    <cse-list>
      <cse-list-item variant="unstyled">
        <cse-skeleton height="96" width="350" index="0" variant="secondary"></cse-skeleton>
      </cse-list-item>
      <cse-list-item variant="unstyled">
        <cse-skeleton height="96" width="350" index="1" variant="secondary"></cse-skeleton>
      </cse-list-item>
      <cse-list-item variant="unstyled">
        <cse-skeleton height="96" width="350" index="2" variant="secondary"></cse-skeleton>
      </cse-list-item>
    </cse-list>
  `

  private renderEmpty = () => html`
    <cse-text id="empty-state" variant="body">
      ${(this.locations ?? []).length === 0
        ? t('no_stores_found', { ns: 'csc-store-stock', default: 'No stores found matching your search' })
        : t('no_stores_in_stock', { ns: 'csc-store-stock', default: 'No stores currently have this product in stock' })}
    </cse-text>
  `

  private renderStock = (location: InternalStoreStockLocation) => {
    if (location.stock === null || location.stock === undefined) {
      return html`<cse-skeleton slot="stock" height="24" width="120" variant="secondary"></cse-skeleton>`
    }

    return html`<csc-stock-indication slot="stock" stock="${location.stock}" variant="simple"></csc-stock-indication>`
  }

  private renderList = (locations: InternalStoreStockLocation[]) => html`
    <cse-list class="csc-store-stock-list" @store-location-selected="${this.handleLocationSelected}">
      ${repeat(locations, (location) => location.id, (location) => html`
        <cse-list-item variant="unstyled">
          <csc-store-location
            .store="${location}"
            hidden-fields="${this.showDistance ? nothing : 'distance'}"
            ?selected="${location.id === this.selectedStoreId}"
          >
            ${this.renderStock(location)}
          </csc-store-location>
        </cse-list-item>
      `)}
    </cse-list>
  `

  override render() {
    const visibleLocations = this.visibleLocations

    if (this.locations === null) return this.renderLoading()
    if (visibleLocations.length === 0) return this.renderEmpty()
    return this.renderList(visibleLocations)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-store-stock-list': CSCStoreStockList
  }
}
